import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Trash2 } from "lucide-react";
import { api, type MediaAsset } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MediaThumb } from "@/components/MediaThumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const MODES = [
  { id: "as_is", label: "Jak jest" },
  { id: "background", label: "Tło" },
  { id: "person_ref", label: "Osoba w nowym kontekście" },
];

const TABS = [
  { id: "recs", label: "Rekomendacje" },
  { id: "library", label: "Biblioteka" },
  { id: "generated", label: "Wygenerowane" },
] as const;

export default function PhotoPickerModal({
  brief,
  onPick,
  onClose,
}: {
  brief?: {
    pain_id?: string;
    tool_id?: string;
    book_id?: string;
    funnel_stage?: string;
    title?: string;
    photo_kind?: string;
    copy_text?: string;
  };
  onPick: (asset: MediaAsset, mode: string) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("recs");
  const [mode, setMode] = useState("as_is");
  const [q, setQ] = useState("");
  const suggestQ = useQuery({
    queryKey: ["media-suggest", brief, q, mode],
    queryFn: () =>
      api.mediaSuggest({
        ...brief,
        q,
        mode,
        need_person: mode === "person_ref" ? "yes" : "",
        limit: 8,
      }),
  });
  const listQ = useQuery({
    queryKey: ["media-library", q],
    queryFn: () =>
      api.mediaList({
        q,
        kind: "image",
        exclude_origin: "generated",
        exclude_status: "rejected",
        limit: 200,
      }),
  });
  const generatedQ = useQuery({
    queryKey: ["media-generated", q],
    queryFn: () => api.mediaList({ origin: "generated", q, kind: "image", limit: 200 }),
  });
  const suggested = suggestQ.data?.items || [];
  const library = listQ.data?.items || [];
  const generated = generatedQ.data?.items || [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["media"] });
    qc.invalidateQueries({ queryKey: ["media-library"] });
    qc.invalidateQueries({ queryKey: ["media-generated"] });
    qc.invalidateQueries({ queryKey: ["media-suggest"] });
  };

  const pickM = useMutation({
    mutationFn: async (asset: MediaAsset) => {
      if (asset.status && asset.status !== "ready" && asset.status !== "rejected") {
        try {
          await api.mediaReady(asset.id);
        } catch {
          /* i tak wybierz */
        }
      }
      onPick(asset, mode);
    },
  });
  const starM = useMutation({
    mutationFn: (asset: MediaAsset) => api.mediaPatch(asset.id, { starred: !asset.starred }),
    onSuccess: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.mediaDelete(id),
    onSuccess: invalidate,
  });
  const uploadM = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        await api.mediaUpload(file);
      }
    },
    onSuccess: () => {
      invalidate();
      setTab("library");
    },
  });

  const gridProps = {
    onPick: (a: MediaAsset) => pickM.mutate(a),
    onStar: (a: MediaAsset) => starM.mutate(a),
    onDelete: (a: MediaAsset) => {
      if (window.confirm("Usunąć tę grafikę z biblioteki?")) deleteM.mutate(a.id);
    },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-xl border border-border bg-card p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold">Wybierz zdjęcie</h2>
            <p className="text-xs text-muted-foreground">
              Rekomendacje z copy karuzeli, cała wrzutka, albo tła z Gemini.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={onClose}>
            Zamknij
          </Button>
        </div>
        <div className="mb-3 flex flex-wrap gap-2">
          {MODES.map((row) => (
            <Button
              key={row.id}
              size="sm"
              variant={mode === row.id ? "brand" : "outline"}
              onClick={() => setMode(row.id)}
            >
              {row.label}
            </Button>
          ))}
          <input
            className="min-w-[160px] flex-1 rounded-md border border-input bg-transparent px-2 text-sm"
            placeholder="szukaj tagu albo nazwy…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Tabs value={tab} onValueChange={(value) => setTab(value as (typeof TABS)[number]["id"])}>
          <TabsList className="w-full justify-start">
            {TABS.map((row) => (
              <TabsTrigger key={row.id} value={row.id}>
                {row.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="recs">
            {suggestQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Szukam kadrów do tej treści…</p>
            ) : suggested.length ? (
              <Grid items={suggested} showScore {...gridProps} />
            ) : (
              <p className="text-sm text-muted-foreground">Nic nie pasuje do tej treści. Weź z Biblioteki.</p>
            )}
          </TabsContent>
          <TabsContent value="library">
            <div className="mb-3 flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  if (files.length) uploadM.mutate(files);
                }}
              />
              <Button size="sm" variant="outline" disabled={uploadM.isPending} onClick={() => fileRef.current?.click()}>
                {uploadM.isPending ? "Wrzucam…" : "Wrzuć zdjęcia"}
              </Button>
              {uploadM.isError ? (
                <span className="text-xs text-destructive">
                  {uploadM.error instanceof Error ? uploadM.error.message : "Błąd wrzutki"}
                </span>
              ) : null}
            </div>
            {library.length ? (
              <Grid items={library} {...gridProps} />
            ) : (
              <p className="text-sm text-muted-foreground">Brak wrzutek. Wrzuć tu albo wejdź w Zdjęcia.</p>
            )}
          </TabsContent>
          <TabsContent value="generated">
            {generated.length ? (
              <Grid items={generated} {...gridProps} />
            ) : (
              <p className="text-sm text-muted-foreground">Brak wygenerowanych teł.</p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Grid({
  items,
  onPick,
  onStar,
  onDelete,
  showScore = false,
}: {
  items: MediaAsset[];
  onPick: (asset: MediaAsset) => void;
  onStar: (asset: MediaAsset) => void;
  onDelete: (asset: MediaAsset) => void;
  showScore?: boolean;
}) {
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {items.map((asset) => (
        <div key={asset.id} className="overflow-hidden rounded-md border border-border">
          <button type="button" className="block w-full text-left hover:border-brand" onClick={() => onPick(asset)}>
            <MediaThumb asset={asset} />
            <div className="p-1.5 text-[11px] leading-snug">
              <div className="truncate font-medium">{asset.original_name || asset.id.slice(0, 8)}</div>
              <div className="text-muted-foreground">
                {[
                  asset.status !== "ready" ? asset.status : "",
                  asset.usable_as,
                  asset.person,
                  asset.funnel_fit,
                  showScore && asset.score != null ? `${asset.score} pkt` : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </button>
          <div className="flex items-center justify-end gap-1 border-t border-border px-1 py-1">
            <button
              type="button"
              title={asset.starred ? "Zdejmij gwiazdkę" : "Gwiazdka"}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-md",
                asset.starred ? "text-brand" : "text-muted-foreground hover:text-foreground",
              )}
              onClick={(e) => {
                e.stopPropagation();
                onStar(asset);
              }}
            >
              <Star className={cn("h-3.5 w-3.5", asset.starred ? "fill-current" : "")} />
            </button>
            <button
              type="button"
              title="Usuń"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(asset);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
